/**
 * NGワードチェック処理
 * 指定した文字列に、指定した文字列リストの値が含まれているかをチェックし、
 * 含まれていた場合は、その文字列を文字列リスト(最大6件)として返します。
 * @param checkTargetString NGワードを含んでいるかどうかのチェックする対象の文字列
 * @param ngWordList NGワードのリスト
 * @returns チェックした結果のNGワードのリスト
 */
export const getNgWordList = (checkTargetString: string, ngWordList: string[]): string[] => {
  const checkResultWords: string[] = [];

  // チェック対象の文字列がブランクまたは、禁則ワードのリストが空である場合
  if (checkTargetString === '' || ngWordList.length === 0) {
    // 空を返します
    return checkResultWords;
  }
  for (const ngWord of ngWordList) {
    // 禁則文字、NGワードを互いに小文字に変換し、値を比較します
    if (toLowerCaseOnlyAlphabet(checkTargetString).includes(toLowerCaseOnlyAlphabet(ngWord))) {
      checkResultWords.push(ngWord);
    }
  }
  return checkResultWords;
};

/**
 * 改行コードを全て削除した文字列を返します
 * @param inputString
 * @returns 改行コードを削除した文字列
 */
export const getStringWithoutLineFeeds = (inputString: string): string => {
  return inputString.replace(/(\r\n|\r|\n)/g, '');
};

/**
 * ブランクを全て削除した文字列を返します
 * ブランクの定義
 * 半角スペース
 * 全角スペース
 * @param inputString
 * @returns ブランクを削除した文字列
 */
export const getStringWithoutBlank = (inputString: string): string => {
  return inputString.replace(/\s+/g, '');
};

/**
 * タブを全て削除した文字列を返します
 * @param inputString
 * @returns タブを削除した文字列
 */
export const getStringWithoutTab = (inputString: string): string => {
  return inputString.replace(/(\t)/g, '');
};

/**
 * 指定した除外対象の文字列配列の値に一致する箇所を、
 * 全て削除した文字列を返します
 * @param inputString
 * @returns タブを削除した文字列
 */
export const excludeStrings = (inputString: string, exclusions: string[]): string => {
  // チェック対象の文字列がブランクまたは、除外対象の文字列配列が空である場合
  if (inputString === '' || exclusions.length === 0) {
    // そのまま(空)を返します
    return inputString;
  }
  let output = inputString;
  for (const exclusion of exclusions) {
    const regex = new RegExp(exclusion, 'g');
    output = output.replace(regex, '');
  }
  return output;
};

/**
 * 文字列内のアルファベットを全て小文字に置換した文字列を返します
 * @param str
 * @returns アルファベットを小文字に変換した文字列
 */
export const toLowerCaseOnlyAlphabet = (str: string) => {
  return str.replace(/[A-Z]/g, (match) => String.fromCharCode(match.charCodeAt(0) + 32));
};

/**
 * 文字列からYYYY年MM月DD日を返します
 * @param date string
 * @returns
 */
export const setStringToDateFormatJapanese = (date: string): string => {
  const dateStrLength = date.length;
  if (dateStrLength < 6) return '';

  switch (dateStrLength) {
    case 6:
      return date.substring(0, 4) + '年' + date.substring(4, 6) + '月';
    case 8:
      return date.substring(0, 4) + '年' + date.substring(4, 6) + '月' + date.substring(6, 8) + '日';
    default:
      return '';
  }
};

/**
 * 単語に全角の括弧を付ける。
 * @param docOfString string
 * @returns
 */
export const setStringToBrackets = (docOfString: string): string => {
  if (docOfString.length === 0) return '';
  return `（${docOfString}）`;
};

// 3桁カンマ区切りとする.
export const setAmountComma = (amount: string) => {
  return String(amount).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
};

/**
 * Base64形式として文字列を置換します
 * @param prev
 * @returns 空文字,undefined,Base64HeaderJpegの場合はブランクを返却、
 */
export const addBase64Header = (prev?: string): string => {
  const Base64HeaderJpeg = 'data:image/jpeg;base64,';
  if (!prev) {
    return '';
  }
  if (Base64HeaderJpeg === prev.substring(0, Base64HeaderJpeg.length)) {
    return prev;
  } else {
    return Base64HeaderJpeg + prev;
  }
};

export const convertStringToNumber = (str: string): number => {
  const parsedNumber = Number(str);
  if (isNaN(parsedNumber)) {
    return 0;
  }
  return parsedNumber;
};

/**
 * 定数内{n}を指定したパラメータでバインドした結果の文字列を返却します \
 * ※定数のパラメータシンボル"{n}"nは0と自然数で定義してください \
 *  例 \
 *   input \
 *     baseString "{0}"は"{1}"です \
 *     args ['これ','テスト'] \
 *   return => これはテストです
 * @param baseString パラメータバインドを行うベースとなる文字列
 * @param args 可変長の文字列配列
 * @returns パラメータバインドされた文字列
 */
export const getReplacedStringByParams = (baseString: string, ...args: string[]): string => {
  let response = baseString;
  args.forEach((arg, index) => {
    response = response.replace('{' + index + '}', arg);
  });
  return response;
};

/**
 * 全角文字チェック
 * @param target 対象文字列
 * @returns 全角文字含む:true/全角文字含まない:false
 */
export const hasZenkaku = (target: string): boolean => {
  for (const char of target) {
    if (char.charCodeAt(0) >= 0x3000) {
      return true;
    }
  }
  return false;
};

/**
 * 文字列を指定した文字数で配列として分割します \
 * ※半角の場合は1文字を1として、全角の場合は1文字を2として文字数をカウントします \
 *  例(半角だけの場合) \
 *   input \
 *     input 'abcdefg' \
 *     maxLength 2 \
 *   return => ['ab','cd','ef','g']
 *  例(半角全角混在の場合) \
 *   input \
 *     input 'abあcいdefうgえお' \
 *     maxLength 4 \
 *   return => ['abあ','cい','def','うg','えお']
 * @param input 配列分割を行う対象の文字列
 * @param maxLength 配列1要素の最大文字数
 * @returns 配列に分割された結果 string[]
 */
export const splitStringByLength = (input: string, maxLength: number): string[] => {
  // 配列分割を行う対象の文字列がブランクの場合は空の配列を返却します
  if (input.length === 0) {
    return [];
  }
  // 配列1要素の最大文字数は"自然数"である必要があるため、1より小さい数値の場合は、分割せずに、1要素として返却します
  if (maxLength < 1) {
    return [input];
  }

  // 配列1要素の最大文字数が1である場合、全角文字を2文字とカウントする仕様上、正しく分割できないため、
  // 全角文字を含むかつ、最大文字数が1である場合は。分割せずに、1要素として返却します
  if (maxLength === 1 && hasZenkaku(input)) {
    return [input];
  }

  const result: string[] = [];
  let currentChunk = '';
  let currentLength = 0;

  for (const char of input) {
    // 全角文字は2、半角は1文字としてカウントします
    // ※文字コードが0x3000以上の場合は全角文字とみなします
    const charLength = char.charCodeAt(0) >= 0x3000 ? 2 : 1;

    // 現在のチャンクの長さに、カウント結果を加えた際、最大文字数を超える場合
    if (currentLength + charLength > maxLength) {
      // 現在のチャンクを、返却用の配列に追加し、新しいチャンクを開始します
      result.push(currentChunk);
      currentChunk = '';
      currentLength = 0;
    }

    // 現在の文字をチャンクに追加します
    currentChunk += char;
    currentLength += charLength;
  }

  // 最後に残ったチャンクを、返却用の配列に追加します
  result.push(currentChunk);

  return result;
};
